import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0D14]">
      <div
        className="hidden lg:flex flex-1 relative items-end p-12 bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(to top, rgba(10,13,20,0.95), rgba(10,13,20,0.4)), url('https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
        }}
      >
        <div className="max-w-md">
          <h1 className="font-heading text-5xl font-extrabold text-white leading-tight">
            Pilotez votre atelier
          </h1>
          <p className="text-[#94A3B8] mt-4 text-lg">
            Stock pièces & pneus, suivi des réparations et devis — le tout au même endroit.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-[480px] flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#F59E0B] flex items-center justify-center">
              <Car className="w-7 h-7 text-[#0A0D14]" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-xl text-[#F8FAFC]">MA SERVICES</div>
              <div className="text-xs text-[#64748B] tracking-widest uppercase">Gestion Atelier</div>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-[#F8FAFC] mb-1">Connexion</h2>
          <p className="text-sm text-[#94A3B8] mb-6">Accédez à votre espace de gestion.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[#94A3B8]">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-[#182232] border-[#2A364F] text-[#F8FAFC]"
                placeholder="vous@garage.fr"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Mot de passe</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 bg-[#182232] border-[#2A364F] text-[#F8FAFC]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#3B82F6] hover:underline">
              Mot de passe oublié ?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
